<template>
  <div class="input" :class="[$attrs.class, { 'input--error': error, 'input--disabled': disabled }]">
    <Label
      v-if="!hideLabel"
      class="input__label"
      :class="{ 'input__label--error': error }"
      :for="id"
      :required="required"
      ><span class="input__label-text">{{ label }}</span
      ><span class="input__label-required" v-if="required" aria-hidden="true">*</span></Label
    >
    <div class="input__field-wrapper flex items-center gap-2 w-full relative">
      <div class="input__field-prefix absolute left-3" v-if="$slots.prefix">
        <slot name="prefix" />
      </div>
      <input
        class="input__field flex-1 w-full"
        :class="inputClasses"
        :type="type"
        :id="id"
        :name="name"
        :required="required"
        :placeholder="placeholder"
        :value="modelValue"
        :disabled="disabled"
        :aria-invalid="error ? 'true' : 'false'"
        :aria-describedby="error ? `${id}-error` : undefined"
        :aria-label="label"
        :aria-required="required"
        :autocomplete="autocomplete"
        :tabindex="tabindex"
        :min="min"
        :max="max"
        :step="step"
        @input="handleInput"
        @blur="handleBlur"
      />
      <div class="input__field-suffix absolute right-3" v-if="$slots.suffix">
        <slot name="suffix" />
      </div>
    </div>
    <p class="input__error" :id="`${id}-error`" v-if="error">{{ error }}</p>
  </div>
</template>

<script lang="ts" setup>
  import { Label } from 'reka-ui';

  const props = defineProps<{
    label?: string;
    hideLabel?: boolean;
    id: string;
    name: string;
    type: string;
    required?: boolean;
    placeholder?: string;
    autocomplete?: string;
    disabled?: boolean;
    error?: string;
    modelValue?: string | number;
    inputClass?: string;
    tabindex?: number;
    min?: number;
    max?: number;
    step?: number;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string | number): void;
    (e: 'blur'): void;
  }>();

  const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('update:modelValue', target.value);
  };

  const handleBlur = () => {
    emit('blur');
  };

  const slots = useSlots();

  const inputClasses = computed(() => {
    const classes = [
      props.inputClass,
      { 'input__field--error': props.error, 'input__field--disabled': props.disabled },
    ];

    if (slots.prefix) {
      classes.push('input__field-prefix pl-10');
    }

    if (slots.suffix) {
      classes.push('input__field-suffix pr-10');
    }
    return classes;
  });
</script>

<style>
  .input {
    @apply w-full flex flex-col gap-2;

    &__field {
      &--error {
        @apply text-[#CF2402] border-[#CF2402];
      }
    }

    &__error {
      @apply text-small-normal;
      color: #cf2402;
    }
  }
</style>
